import React, { useEffect, useState, useRef, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  MdAdd,
  MdDeleteOutline,
  MdLogout,
  MdEdit,
  MdSearch
} from "react-icons/md";
import { useClientes } from '../../../services/hooks/useClientes';
import { Modal } from "antd";
import ModalCliente from '../../../components/modals/ModalCliente';
import { useTipoDocumentos } from '../../../services/hooks/useTipoDocumentos';
import "../../../components/css/Clientes.css";

const MantenimientoClientes = () => {
  const navigate = useNavigate();
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));
  
  // Usar los nuevos hooks
  const { clientes, createCliente, updateCliente, deleteCliente: deleteClienteApi, fetchClientes: fetchClientesHook } = useClientes();
  const { tipoDocumentos, fetchTipoDocumentos } = useTipoDocumentos();
  
  const [state, setState] = useState({
    searchTerm: "",
    clienteSeleccionado: null,
    openModalPost: false,
    openModalPut: false
  });
  const [showModalCliente, setShowModalCliente] = useState(false);
  const [clienteEditar, setClienteEditar] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clienteToDelete, setClienteToDelete] = useState(null);
  const hasFetched = useRef(false);

  // Cargar solo clientes al inicializar la página
  const fetchClientes = useCallback(async () => {
    try {
      await fetchClientesHook();
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  }, [fetchClientesHook]);

  useEffect(() => {
    if (selectedProject && selectedProject.id && !hasFetched.current) {
      hasFetched.current = true;
      fetchClientes();
    }
  }, [selectedProject, fetchClientes]);

  // Actualizar cliente seleccionado cuando la lista de clientes se actualiza
  // Esto asegura que siempre tengamos la versión más reciente del cliente seleccionado
  useEffect(() => {
    if (state.clienteSeleccionado?.id) {
      const clienteActualizado = clientes.find(c => c.id === state.clienteSeleccionado.id);
      if (clienteActualizado) {
        // Comparar si los datos realmente cambiaron para evitar actualizaciones innecesarias
        const datosCambiaron = JSON.stringify(clienteActualizado) !== JSON.stringify(state.clienteSeleccionado);
        if (datosCambiaron) {
          setState(prev => ({ ...prev, clienteSeleccionado: clienteActualizado }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientes]);

  // Función para abrir modal de creación
  const handleOpenCreateModal = async () => {
    try {
      // Cargar tipos de documento solo cuando se necesiten
      await fetchTipoDocumentos();
      setClienteEditar(null);
      setShowModalCliente(true);
    } catch (error) {
      console.error("Error al cargar tipos de documento:", error);
      toast.error("Error al cargar tipos de documento", {
        position: "bottom-right",
        autoClose: 2000
      });
    }
  };

  // Función para abrir modal de edición
  const handleOpenEditModal = async (cliente) => {
    try {
      // Cargar tipos de documento solo cuando se necesiten
      await fetchTipoDocumentos();
      
      // Buscar el cliente actualizado en la lista para obtener los datos más recientes
      const clienteActualizado = clientes.find(c => c.id === cliente.id);
      
      if (clienteActualizado) {
        setClienteEditar(clienteActualizado);
        // Actualizar también el cliente seleccionado con los datos actualizados
        setState(prev => ({ ...prev, clienteSeleccionado: clienteActualizado }));
        setShowModalCliente(true);
      } else {
        toast.error("No se pudo encontrar el cliente actualizado", {
          position: "bottom-right",
          autoClose: 2000
        });
      }
    } catch (error) {
      console.error("Error al cargar tipos de documento:", error);
      toast.error("Error al cargar tipos de documento", {
        position: "bottom-right",
        autoClose: 2000
      });
    }
  };

  const handleSearch = (event) => {
    setState(prev => ({ ...prev, searchTerm: event.target.value }));
  };

  const handleClienteSelect = (cliente) => {
    setState(prev => ({ 
      ...prev, 
      clienteSeleccionado: prev.clienteSeleccionado?.id === cliente.id ? null : cliente 
    }));
  };

  const handleDelete = (id) => {
    if (!id) {
      toast.error("Por favor seleccione un cliente para eliminar", {
        position: "bottom-right",
        autoClose: 2000
      });
      return;
    }

    setClienteToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!clienteToDelete) return;

    // Cerrar modal inmediatamente
    setShowDeleteModal(false);
    
    // La eliminación se maneja completamente por el hook useClientes
    // No necesitamos manipular estado local
    
    try {
      // Eliminar en el backend - el hook ya maneja las notificaciones
      await deleteClienteApi(clienteToDelete);
      
    } catch (error) {
      console.error("Error al eliminar:", error);
      // El hook useClientes ya maneja los errores con toasts automáticamente
      
    } finally {
      setClienteToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setClienteToDelete(null);
  };

  const filteredClientes = (clientes || []).filter((cliente) =>
    (cliente.nombre || cliente.nombre_cliente || "").toLowerCase().includes(state.searchTerm.toLowerCase())
  );

  return (
    <>
      {/* Digital Harmony Styles para Mantenimiento Clientes */}
      <style>
        {`
          /* Fondo y contenedor principal */
          .dh-clientes-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          /* Card principal con Digital Harmony */
          .dh-card-main {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          
          /* Header del card */
          .dh-card-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
          }
          
          .dh-header-title {
            color: white;
            font-weight: 700;
            font-size: 1.5rem;
            margin: 0;
          }
          
          /* Botones con Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-warning {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(240, 147, 251, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-warning:hover {
            background: linear-gradient(135deg, #f5576c 0%, #e91e63 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(240, 147, 251, 0.4);
            color: white;
          }
          
          .btn-dh-danger {
            background: linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(255, 65, 108, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #ff4b2b 0%, #e53e3e 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(255, 65, 108, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          /* Campo de búsqueda moderno */
          .dh-search-container {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-radius: 15px;
            padding: 1rem;
            margin-bottom: 1.5rem;
          }
          
          .dh-input-group {
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
          }
          
          .dh-input-group .input-group-text {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 12px 15px;
          }
          
          .dh-input-group .form-control {
            border: none;
            padding: 12px 15px;
            font-size: 1rem;
            background: white;
          }
          
          .dh-input-group .form-control:focus {
            box-shadow: none;
            background: white;
          }
          
          /* Tabla con Enhanced Table style */
          .dh-table {
            border-collapse: separate;
            border-spacing: 0;
            background: white;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
          }
          
          .dh-table thead {
            background: #374151 !important;
          }
          
          .dh-table thead th {
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.875rem !important;
            letter-spacing: 0.025em !important;
            text-transform: uppercase !important;
            border: none !important;
            padding: 1rem 0.75rem !important;
            position: relative !important;
            background: none !important;
          }
          
          /* Forzar visibilidad de texto en headers */
          .dh-table thead tr th,
          .dh-table thead tr th span,
          .dh-table thead tr th .header-icon {
            color: white !important;
            opacity: 1 !important;
            visibility: visible !important;
          }
          
          .dh-table tbody tr {
            transition: all 0.2s ease-in-out;
            border-bottom: 1px solid #f3f4f6;
          }
          
          .dh-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.02) 100%);
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(0,0,0,0.06);
          }
          
          .dh-table tbody tr.table-primary {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
            border-left: 4px solid #667eea;
            box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
          }
          
          .dh-table tbody td {
            padding: 1rem 0.75rem;
            vertical-align: middle;
            border: none;
            font-size: 0.95rem;
          }
          
          /* Checkbox personalizado */
          .form-check-input:checked {
            background-color: #667eea;
            border-color: #667eea;
          }
          
          .form-check-input:focus {
            box-shadow: 0 0 0 0.25rem rgba(102, 126, 234, 0.25);
          }
          
          /* Estado vacío mejorado */
          .dh-empty-state {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border: 2px dashed #d1d5db;
            border-radius: 15px;
            padding: 3rem 2rem;
            text-align: center;
            color: #6b7280;
          }
          
          /* Loading state */
          .dh-loading {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-radius: 15px;
            padding: 3rem 2rem;
            text-align: center;
          }
        `}
      </style>
      
      <div className="dh-clientes-bg">
        <div className="container-fluid py-4">
          <ToastContainer />
          <div className="dh-card-main">
            <div className="dh-card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="dh-header-title">Mantenimiento de Clientes</h5>
                <div className="btn-group">
              <button
                onClick={handleOpenCreateModal}
                className="btn-dh-primary d-flex align-items-center gap-2"
                title="Agregar un nuevo cliente"
              >
                <MdAdd size={20} />
                Nuevo Cliente
              </button>
              <button
                onClick={() => {
                  if (state.clienteSeleccionado) {
                    handleOpenEditModal(state.clienteSeleccionado);
                  } else {
                    toast.error("Por favor seleccione un cliente para editar", {
                      position: "bottom-right",
                      autoClose: 2000
                    });
                  }
                }}
                className="btn-dh-warning d-flex align-items-center gap-2"
                title="Editar cliente seleccionado"
              >
                <MdEdit size={20} />
                Editar
              </button>
              <button
                onClick={() => handleDelete(state.clienteSeleccionado?.id)}
                className="btn-dh-danger d-flex align-items-center gap-2"
                title="Eliminar cliente seleccionado"
              >
                <MdDeleteOutline size={20} />
                Eliminar
              </button>
              <button
                onClick={() => {
                  navigate("/home");
                }}
                className="btn-dh-secondary d-flex align-items-center gap-2"
                title="Volver al inicio"
              >
                <MdLogout size={20} />
                Cerrar
              </button>
            </div>
          </div>
        </div>

            <div className="card-body" style={{ padding: '2rem' }}>
              <div className="dh-search-container">
                <div className="dh-input-group input-group">
                  <span className="input-group-text">
                    <MdSearch size={20} />
                  </span>
                  <input
                    type="search"
                    className="form-control"
                    placeholder="Buscar cliente..."
                    value={state.searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>

              <div className="table-responsive">
                <table className="dh-table table">
                  <thead>
                <tr>
                  <th style={{ width: "50px" }}></th>
                  <th>Nombre del Cliente</th>
                  <th>Tipo Documento</th>
                  <th>Número Documento</th>
                </tr>
              </thead>
                  <tbody>
                    {state.loading ? (
                      <tr>
                        <td colSpan="4" className="p-0">
                          <div className="dh-loading">
                            <div className="d-flex justify-content-center align-items-center">
                              <div className="spinner-border text-primary me-3" role="status">
                                <span className="visually-hidden">Cargando...</span>
                              </div>
                              <span className="text-muted">Cargando clientes...</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : filteredClientes.length > 0 ? (
                      filteredClientes.map((cliente) => (
                        <tr 
                          key={cliente.id}
                          className={state.clienteSeleccionado?.id === cliente.id ? 'table-primary' : ''}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleClienteSelect(cliente)}
                        >
                          <td>
                            <div className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={state.clienteSeleccionado?.id === cliente.id}
                                onChange={() => {}}
                              />
                            </div>
                          </td>
                          <td style={{ fontWeight: '600' }}>{cliente.nombre}</td>
                          <td>{cliente.tipo_documento}</td>
                          <td>{cliente.numero_documento}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="p-0">
                          <div className="dh-empty-state">
                            <i className="fas fa-users" style={{ fontSize: '3rem', marginBottom: '1rem', opacity: '0.5' }}></i>
                            <h5 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>No hay clientes registrados</h5>
                            <p style={{ margin: '0', opacity: '0.8' }}>
                              Haga clic en "Nuevo Cliente" para crear su primer cliente
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalCliente
        show={showModalCliente}
        onHide={() => setShowModalCliente(false)}
        cliente={clienteEditar}
        documentos={tipoDocumentos || []}
        onSave={async (data) => {
          try {
            if (clienteEditar) {
              await updateCliente(clienteEditar.id, data);
            } else {
              await createCliente(data);
            }
            setShowModalCliente(false);
            setClienteEditar(null);
            // No necesitamos fetchClientes() porque el hook ya actualiza la lista
          } catch (error) {
            console.error("Error al guardar:", error);
            // El hook ya maneja los errores con toasts, no necesitamos duplicar
          }
        }}
      />

      <Modal
        title="Confirmar eliminación"
        open={showDeleteModal}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="Eliminar"
        cancelText="Cancelar"
        okType="danger"
        style={{ top: 'auto', bottom: 20 }}
        getContainer={() => document.body}
      >
        <p>¿Estás seguro de que quieres eliminar este cliente?</p>
        <p>Esta acción no se puede deshacer.</p>
      </Modal>
    </>
  );
};

export default MantenimientoClientes;
